<template>
  <v-card flat class="border-0 !bg-[rgb(var(--v-theme-surface))]">
    <v-card-title class="d-flex align-center gap-1">
      Step 3: Record Attorney Fee
      <LlTooltip
        v-if="redesignEnabled"
        max-width="260"
        text="The fee is calculated automatically. Confirm to record it — expand below to edit the description, date, or rounding."
      >
        <template #activator>
          <v-icon size="16" color="grey" class="cursor-pointer">mdi-information-outline</v-icon>
        </template>
      </LlTooltip>
    </v-card-title>
    <v-card-text>
      <!-- Loading state -->
      <v-skeleton-loader
        v-if="loading"
        type="paragraph, paragraph"
        class="mb-4"
      />

      <!-- ── OLD DESIGN ─────────────────────────────────── -->
      <template v-if="!redesignEnabled && !loading">
        <!-- Mismatch warning — top of step per mockup -->
        <AlertBar
          v-if="feeMatchStatus === 'mismatch'"
          class="mb-4"
        >
          <strong>Selected Fee Will Be Updated</strong>
          <p class="mt-1 mb-0">
            The selected fee of <strong>{{ formatCurrency(selectedFeeAmount) }}</strong> will be updated to <strong>{{ formatCurrency(calculatedFee ?? 0) }}</strong> when you confirm below.
          </p>
        </AlertBar>

        <!-- Fee method explanation -->
        <p class="text-body-2 text-grey-darken-1 mb-3">
          <template v-if="feeBreakdown.variant === 'gross'">
            Using the <strong>Gross</strong> method, the fee is calculated on the full settlement amount. Only <strong>unreimbursed costs</strong> are deducted from the client's share.
          </template>
          <template v-else-if="feeBreakdown.variant === 'net'">
            Using the <strong>Net</strong> method, <strong>total {{ expenseLabel }}</strong> are deducted first, then the fee is calculated on the remaining amount.
          </template>
          <template v-else-if="feeBreakdown.variant === 'absorbed-gross'">
            The firm <strong>absorbs all expenses</strong> from the fee. Using the <strong>Gross</strong> method, the fee is calculated on the full settlement, then expenses are deducted from it. The client pays no costs.
          </template>
          <template v-else-if="feeBreakdown.variant === 'absorbed-net'">
            The firm <strong>absorbs all expenses</strong>. Using the <strong>Net</strong> method, expenses reduce the fee base — the fee is calculated on the remaining amount. The client pays no costs.
          </template>
        </p>

        <!-- Calculation breakdown table -->
        <div
          v-if="feeBreakdown.summary"
          class="bg-grey-lighten-5 border rounded-lg pa-4 mb-4"
        >
          <!-- Gross method breakdown -->
          <template v-if="feeBreakdown.variant === 'gross'">
            <div class="flex justify-between py-1 px-2">
              <span class="text-body-2 text-grey-darken-2">Recovery Amount</span>
              <span class="text-body-2 font-weight-bold" style="font-variant-numeric: tabular-nums">{{ formatCurrency(feeBreakdown.summary.recoveryAmount ?? 0) }}</span>
            </div>
            <div class="flex justify-between py-1 px-2">
              <span class="text-body-2 text-grey-darken-2">Attorney Fee ({{ feeBreakdown.pctLabel }})</span>
              <span class="text-body-2 font-weight-bold" style="font-variant-numeric: tabular-nums">&minus;{{ formatCurrency(feeBreakdown.summary.attorneyFee ?? 0) }}</span>
            </div>
            <div class="px-2 py-0">
              <span class="text-caption text-grey font-italic">Fee = {{ formatCurrency(feeBreakdown.summary.recoveryAmount ?? 0) }} &times; {{ feeBreakdown.pctLabel }}<span v-if="roundToNearestDollar"> - rounded to the nearest dollar</span></span>
            </div>
            <div class="flex justify-between py-1 px-2">
              <span class="text-body-2 text-grey-darken-2">Less: {{ expenseLabel }}</span>
              <span class="text-body-2 font-weight-bold" style="font-variant-numeric: tabular-nums">&minus;{{ formatCurrency(feeBreakdown.summary.totalExpenses ?? 0) }}</span>
            </div>
            <v-divider class="my-1 mx-2 border-2 border-gray-700" />
            <div class="flex justify-between py-1 px-2">
              <span class="text-body-1 font-weight-bold">Attorney Fee to Record</span>
              <span class="text-body-1 font-weight-bold text-green-darken-2" style="font-variant-numeric: tabular-nums">{{ formatCurrency(feeBreakdown.summary.attorneyFee ?? 0) }}</span>
            </div>
            <div class="flex justify-between py-1 px-2" style="opacity: 0.6">
              <span class="text-caption text-grey-darken-1">Client Net from Settlement</span>
              <span class="text-caption font-weight-medium" style="font-variant-numeric: tabular-nums">{{ formatCurrency(feeBreakdown.summary.clientNetRecovery ?? 0) }}</span>
            </div>
          </template>

          <!-- Net method breakdown -->
          <template v-else-if="feeBreakdown.variant === 'net'">
            <div class="flex justify-between py-1 px-2">
              <span class="text-body-2 text-grey-darken-2">Recovery Amount</span>
              <span class="text-body-2 font-weight-bold" style="font-variant-numeric: tabular-nums">{{ formatCurrency(feeBreakdown.summary.recoveryAmount ?? 0) }}</span>
            </div>
            <div class="flex justify-between py-1 px-2">
              <span class="text-body-2 text-grey-darken-2">Less: {{ expenseLabel }}</span>
              <span class="text-body-2 font-weight-bold" style="font-variant-numeric: tabular-nums">&minus;{{ formatCurrency(feeBreakdown.summary.totalExpenses ?? 0) }}</span>
            </div>
            <div class="flex justify-between py-1 px-2 border-t font-weight-bold mt-1 pt-2">
              <span class="text-body-2">Net for Fee Calculation</span>
              <span class="text-body-2 font-weight-bold" style="font-variant-numeric: tabular-nums">{{ formatCurrency(feeBreakdown.summary.feeBase ?? 0) }}</span>
            </div>
            <div class="flex justify-between py-1 px-2">
              <span class="text-body-2 text-grey-darken-2">Attorney Fee ({{ feeBreakdown.pctLabel }})</span>
              <span class="text-body-2 font-weight-bold" style="font-variant-numeric: tabular-nums">&minus;{{ formatCurrency(feeBreakdown.summary.attorneyFee ?? 0) }}</span>
            </div>
            <div class="px-2 py-0">
              <span class="text-caption text-grey font-italic">Fee = {{ formatCurrency(feeBreakdown.summary.feeBase ?? 0) }} &times; {{ feeBreakdown.pctLabel }}<span v-if="roundToNearestDollar"> - rounded to the nearest dollar</span></span>
            </div>
            <v-divider class="my-1 mx-2 border-2 border-gray-700" />
            <div class="flex justify-between py-1 px-2">
              <span class="text-body-1 font-weight-bold">Attorney Fee to Record</span>
              <span class="text-body-1 font-weight-bold text-green-darken-2" style="font-variant-numeric: tabular-nums">{{ formatCurrency(feeBreakdown.summary.attorneyFee ?? 0) }}</span>
            </div>
            <div class="flex justify-between py-1 px-2" style="opacity: 0.6">
              <span class="text-caption text-grey-darken-1">Client Net from Settlement</span>
              <span class="text-caption font-weight-medium" style="font-variant-numeric: tabular-nums">{{ formatCurrency(feeBreakdown.summary.clientNetRecovery ?? 0) }}</span>
            </div>
          </template>

          <!-- Absorbed + Gross breakdown -->
          <template v-else-if="feeBreakdown.variant === 'absorbed-gross'">
            <div class="flex justify-between py-1 px-2">
              <span class="text-body-2 text-grey-darken-2">Recovery Amount</span>
              <span class="text-body-2 font-weight-bold" style="font-variant-numeric: tabular-nums">{{ formatCurrency(feeBreakdown.summary.recoveryAmount ?? 0) }}</span>
            </div>
            <div class="flex justify-between py-1 px-2">
              <span class="text-body-2 text-grey-darken-2">Gross Fee ({{ feeBreakdown.pctLabel }})</span>
              <span class="text-body-2 font-weight-bold" style="font-variant-numeric: tabular-nums">&minus;{{ formatCurrency(feeBreakdown.summary.attorneyFee ?? grossFeeAmount) }}</span>
            </div>
            <div class="px-2 py-0">
              <span class="text-caption text-grey font-italic">Fee = {{ formatCurrency(feeBreakdown.summary.recoveryAmount ?? 0) }} &times; {{ feeBreakdown.pctLabel }}<span v-if="roundToNearestDollar"> - rounded to the nearest dollar</span></span>
            </div>
            <div class="flex justify-between py-1 px-2">
              <span class="text-body-2 text-grey-darken-2">Less: Firm Expenses (absorbed)</span>
              <span class="text-body-2 font-weight-bold" style="font-variant-numeric: tabular-nums">&minus;{{ formatCurrency(feeBreakdown.summary.totalExpenses ?? 0) }}</span>
            </div>
            <div class="flex justify-between py-1 px-2 border-t font-weight-bold mt-1 pt-2">
              <span class="text-body-2">Net Attorney Fee</span>
              <span class="text-body-2 font-weight-bold" style="font-variant-numeric: tabular-nums">{{ netAttorneyFeeDisplay }}</span>
            </div>
            <div class="px-2 py-0">
              <span class="text-caption text-grey font-italic">Expenses come out of the fee - the client is not charged</span>
            </div>
            <v-divider class="my-1 mx-2 border-2 border-gray-700" />
            <div class="flex justify-between py-1 px-2">
              <span class="text-body-1 font-weight-bold">Attorney Fee to Record</span>
              <span class="text-body-1 font-weight-bold text-green-darken-2" style="font-variant-numeric: tabular-nums">{{ formatCurrency(netAttorneyFeeAmount) }}</span>
            </div>
            <div class="flex justify-between py-1 px-2" style="opacity: 0.6">
              <span class="text-caption text-grey-darken-1">Client Net from Settlement</span>
              <span class="text-caption font-weight-medium" style="font-variant-numeric: tabular-nums">{{ formatCurrency(feeBreakdown.summary.clientNetRecovery ?? 0) }}</span>
            </div>
          </template>

          <!-- Absorbed + Net breakdown -->
          <template v-else-if="feeBreakdown.variant === 'absorbed-net'">
            <div class="flex justify-between py-1 px-2">
              <span class="text-body-2 text-grey-darken-2">Recovery Amount</span>
              <span class="text-body-2 font-weight-bold" style="font-variant-numeric: tabular-nums">{{ formatCurrency(feeBreakdown.summary.recoveryAmount ?? 0) }}</span>
            </div>
            <div class="flex justify-between py-1 px-2">
              <span class="text-body-2 text-grey-darken-2">Less: Firm Expenses (absorbed)</span>
              <span class="text-body-2 font-weight-bold" style="font-variant-numeric: tabular-nums">&minus;{{ formatCurrency(feeBreakdown.summary.totalExpenses ?? 0) }}</span>
            </div>
            <div class="flex justify-between py-1 px-2 border-t font-weight-bold mt-1 pt-2">
              <span class="text-body-2">Net for Fee Calculation</span>
              <span class="text-body-2 font-weight-bold" style="font-variant-numeric: tabular-nums">{{ formatCurrency(feeBreakdown.summary.feeBase ?? 0) }}</span>
            </div>
            <div class="flex justify-between py-1 px-2">
              <span class="text-body-2 text-grey-darken-2">Attorney Fee ({{ feeBreakdown.pctLabel }})</span>
              <span class="text-body-2 font-weight-bold" style="font-variant-numeric: tabular-nums">&minus;{{ formatCurrency(feeBreakdown.summary.attorneyFee ?? 0) }}</span>
            </div>
            <div class="px-2 py-0">
              <span class="text-caption text-grey font-italic">Fee = {{ formatCurrency(feeBreakdown.summary.feeBase ?? 0) }} &times; {{ feeBreakdown.pctLabel }}<span v-if="roundToNearestDollar"> - rounded to the nearest dollar</span></span>
            </div>
            <div class="flex justify-between py-1 px-2">
              <span class="text-body-2 text-grey-darken-2">Less: Total Case Costs (Absorbed)</span>
              <span class="text-body-2 font-weight-bold" style="font-variant-numeric: tabular-nums">&minus;{{ formatCurrency(feeBreakdown.summary.totalExpenses ?? 0) }}</span>
            </div>
            <div class="flex justify-between py-1 px-2 border-t font-weight-bold mt-1 pt-2">
              <span class="text-body-2">Net Attorney Fee</span>
              <span class="text-body-2 font-weight-bold" style="font-variant-numeric: tabular-nums">{{ netAttorneyFeeDisplay }}</span>
            </div>
            <div class="px-2 py-1">
              <span class="text-caption text-green-darken-2 font-italic">Firm absorbs expenses - costs reduce the fee base and come out of the fee. The client is not charged.</span>
            </div>
            <v-divider class="my-1 mx-2 border-2 border-gray-700" />
            <div class="flex justify-between py-1 px-2">
              <span class="text-body-1 font-weight-bold">Attorney Fee to Record</span>
              <span class="text-body-1 font-weight-bold text-green-darken-2" style="font-variant-numeric: tabular-nums">{{ formatCurrency(netAttorneyFeeAmount) }}</span>
            </div>
            <div class="flex justify-between py-1 px-2" style="opacity: 0.6">
              <span class="text-caption text-grey-darken-1">Client Net from Settlement</span>
              <span class="text-caption font-weight-medium" style="font-variant-numeric: tabular-nums">{{ formatCurrency(feeBreakdown.summary.clientNetRecovery ?? 0) }}</span>
            </div>
          </template>
        </div>

        <!-- Attorney Fee Callout -->
        <div class="flex items-center gap-4 mb-4 flex-wrap">
          <div class="border-2 border-green rounded-lg pa-4 bg-green-lighten-5 flex justify-between items-center gap-4 flex-grow-1">
            <div>
              <div class="text-uppercase text-caption font-weight-bold text-grey-darken-1 mb-1">Attorney Fee to Record</div>
              <div class="text-h5 font-weight-bold text-green-darken-3">
                {{ formatCurrency(calculatedFee ?? 0) }}
              </div>
            </div>
            <div>
              <div v-if="feeMatchStatus === 'match'" class="text-caption text-green-darken-2 font-weight-medium">
                <LlIcon icon="mdi-check" size="small" color="success" class="mr-1" />Fee matches
              </div>
              <div v-else-if="feeMatchStatus === 'mismatch'" class="text-caption text-orange-darken-2 font-weight-medium">
                <v-icon size="small" color="orange-darken-2" class="mr-1">mdi-alert</v-icon>Fee mismatch
              </div>
              <div v-else class="text-caption text-amber-darken-2 font-weight-medium">
                <v-icon size="small" color="amber-darken-2" class="mr-1">mdi-alert-outline</v-icon>No matching fee
              </div>
            </div>
          </div>
          <LlCheckbox
            v-model="roundingCheckboxModel"
            label="Round to whole dollar"
            hide-details
            class="ma-0 pa-0"
          />
        </div>

        <!-- Fee Description + Fee Date + Create button on one row (matches prototype) -->
        <div v-if="showCreateFeeForm" class="mb-4 flex gap-3 items-end flex-wrap">
          <div class="flex-grow-1" style="min-width: 180px; flex-basis: 190px">
            <div class="text-uppercase text-caption font-weight-bold text-grey-darken-1 mb-1">
              Fee Description
              <v-tooltip location="top" max-width="280">
                <template #activator="{ props: tooltipProps }">
                  <v-icon v-bind="tooltipProps" size="14" color="grey">mdi-information-outline</v-icon>
                </template>
                This description appears on the fixed fee record and the settlement invoice. Max 255 characters.
              </v-tooltip>
            </div>
            <LlTextField
              v-model="feeDescription"
              :maxlength="255"
              density="compact"
              hide-details
            />
          </div>
          <div style="width: 170px; flex-shrink: 0">
            <div class="text-uppercase text-caption font-weight-bold text-grey-darken-1 mb-1">
              Fee Date
              <v-tooltip location="top" max-width="280">
                <template #activator="{ props: tooltipProps }">
                  <v-icon v-bind="tooltipProps" size="14" color="grey">mdi-information-outline</v-icon>
                </template>
                The date the fee is recorded. You can change this to match the settlement date or fee agreement date.
              </v-tooltip>
            </div>
            <LlDatePicker
              class="fee-date-picker"
              :model-value="feeDateModel"
              label=""
              label-class="d-none"
              @update:model-value="onFeeDateChange"
            />
          </div>
          <div v-if="feeMatchStatus === 'none'" class="flex items-center gap-2">
            <LlSecondaryButton
              color="primary"
              variant="outlined"
              :loading="recording"
              :disabled="!feeDescription.trim()"
              @click="onCreateFee"
            >
              + Create Attorney Fee (&nbsp;{{ formatCurrency(calculatedFee ?? 0) }}&nbsp;)
            </LlSecondaryButton>
            <a
              class="text-primary hover:!text-[rgb(var(--v-theme-primary-darken-1))] hover:underline text-body-2 font-weight-medium cursor-pointer"
              style="white-space: nowrap"
              @click="$emit('manage-fixed-fees')"
            >
              manage fees &rarr;
            </a>
          </div>
        </div>

        <!-- Fixed Fees on This Matter -->
        <div class="mb-4">
          <template v-if="existingFees.length > 0">
            <div class="text-uppercase text-caption font-weight-bold text-grey-darken-1 mb-2">Fixed Fees on This Matter</div>
            <div
              v-for="fee in existingFees"
              :key="fee.chargeId"
              class="border rounded-lg pa-3 mb-2 flex justify-between items-center cursor-pointer hover:bg-grey-lighten-4 transition-colors"
              :class="fee.chargeId === selectedChargeId ? 'border-green bg-green-lighten-5' : ''"
              @click="$emit('select-fee', fee.chargeId)"
            >
              <div class="flex items-center gap-3">
                <LlIcon v-if="fee.chargeId === selectedChargeId" icon="mdi-radiobox-marked" color="primary" size="small" />
                <v-icon v-else color="grey" size="small">mdi-radiobox-blank</v-icon>
                <div>
                  <div class="text-body-2 font-weight-medium">{{ fee.description }}</div>
                  <div class="text-caption text-grey">
                    Fixed Fee
                    <template v-if="fee.feeDate"> &middot; {{ formatFeeDate(fee.feeDate) }}</template>
                    &middot; {{ fee.status }}
                  </div>
                </div>
              </div>
              <div class="text-body-1 font-weight-bold" style="font-variant-numeric: tabular-nums">
                {{ formatCurrency(fee.amount) }}
              </div>
            </div>
          </template>
          <div v-else class="border border-dashed rounded-lg pa-3 text-center text-caption text-grey">
            No fixed fees found. Create one to proceed.
          </div>
        </div>

        <!-- Info box -->
        <AlertBar
          type="info"
          class="mb-4"
        >
          Select or create exactly one fixed fee matching the calculated attorney fee. This fee will appear on the settlement statement and invoice.
        </AlertBar>
      </template>

      <!-- ── NEW REDESIGN ───────────────────────────────── -->
      <template v-else-if="redesignEnabled && !loading">

        <!-- Co-Counsel & Referral Fee Recipients (P4.5) — "no recipients configured".
             Gated on localRecipients still being empty, not just the flag, so a stale
             true from an earlier empty fetch doesn't linger once recipients arrive. -->
        <AlertBar
          v-if="coCounselEnabled && noMatterRecipientsConfigured && localRecipients.length === 0"
          type="info"
          class="mb-4"
        >
          No fee recipients configured. Add recipients in the Fee Recipients tab before this step.
        </AlertBar>

        <!-- Fee mismatch banner — not shown once the fee is split with outside
             recipients (comparing against the old single-recipient amount doesn't
             make sense there). -->
        <AlertBar
          v-if="feeMatchStatus === 'mismatch' && !isFeeSplit"
          class="mb-3"
        >
          <strong>Existing Fee Will Be Updated</strong>
          <p class="mt-1 mb-0">
            A fee of <strong>{{ formatCurrency(existingFeeAmount) }}</strong> already exists on this matter.
            It will be updated to <strong>{{ formatCurrency(calculatedFee ?? 0) }}</strong> when you confirm below.
          </p>
        </AlertBar>

        <!-- Zero-fee notice -->
        <AlertBar
          v-if="isZeroFee"
          type="info"
          class="mb-3"
        >
          <strong>Pro Bono Case</strong>
          <p class="mt-1 mb-0">A 0% fee means no attorney fee will be charged. The statement will generate with a $0.00 fee line.</p>
        </AlertBar>

        <!-- Negative fee base error -->
        <AlertBar
          v-if="hasNegativeFeeBase"
          type="error"
          class="mb-4"
        >
          <strong>Expenses Exceed Recovery</strong>
          <p class="mt-1 mb-0">Total expenses exceed the recovery amount. With the net-of-expenses calculation method, the fee base would be negative. You cannot proceed until expenses are reduced or the recovery amount is increased.</p>
        </AlertBar>

        <!-- Fee callout card -->
        <div class="flex items-center justify-between gap-[14px] py-4 px-[18px] bg-[#f1faf4] border border-[#bfe0c6] rounded-[10px] mb-4">
          <div>
            <div class="text-[10px] font-semibold text-[#747474] uppercase tracking-wider mb-[5px]">Attorney Fee</div>
            <div class="fee-callout-amount text-[26px] font-bold text-[#1b5e20] tabular-nums leading-none">
              {{ calculatedFee == null ? '—' : formatCurrency(calculatedFee) }}
            </div>
          </div>
          <LlSecondaryButton
            size="small"
            variant="outlined"
            :prepend-icon="showCalc ? 'mdi-chevron-up' : 'mdi-chevron-down'"
            class="!bg-[rgb(var(--v-theme-surface))]"
            @click="showCalc = !showCalc"
          >
            {{ showCalc ? 'Hide calculation' : 'Show calculation' }}
          </LlSecondaryButton>
        </div>

        <!-- Collapsible calculation breakdown -->
        <div v-show="showCalc" class="fee-calc-section mb-4">
          <!-- Method explanation -->
          <p class="text-body-2 text-grey-darken-1 mb-3">
            <template v-if="feeBreakdown.variant === 'gross'">
              Using the <strong>Gross</strong> method, the fee is calculated on the full settlement amount. Only <strong>unreimbursed costs</strong> are deducted from the client's share.
            </template>
            <template v-else-if="feeBreakdown.variant === 'net'">
              Using the <strong>Net</strong> method, <strong>total {{ expenseLabel }}</strong> are deducted first, then the fee is calculated on the remaining amount.
            </template>
            <template v-else-if="feeBreakdown.variant === 'absorbed-gross'">
              The firm <strong>absorbs all expenses</strong> from the fee. Using the <strong>Gross</strong> method, the fee is calculated on the full settlement, then expenses are deducted from it. The client pays no costs.
            </template>
            <template v-else-if="feeBreakdown.variant === 'absorbed-net'">
              The firm <strong>absorbs all expenses</strong>. Using the <strong>Net</strong> method, expenses reduce the fee base — the fee is calculated on the remaining amount. The client pays no costs.
            </template>
          </p>

          <!-- Breakdown rows -->
          <div v-if="feeBreakdown.summary" class="bg-[#f7f7f7] border border-[#e5e5e5] rounded-lg p-4 text-[13px]">

            <!-- Gross -->
            <template v-if="feeBreakdown.variant === 'gross'">
              <div class="flex justify-between py-[5px] px-2">
                <span class="text-body-2 text-grey-darken-2">Recovery Amount</span>
                <span class="text-body-2 font-weight-bold tabular-nums">{{ formatCurrency(feeBreakdown.summary.recoveryAmount ?? 0) }}</span>
              </div>
              <div class="flex justify-between py-[5px] px-2">
                <span class="text-body-2 text-grey-darken-2">Attorney Fee ({{ feeBreakdown.pctLabel }})</span>
                <span class="text-body-2 font-weight-bold tabular-nums">&minus;{{ formatCurrency(feeBreakdown.summary.attorneyFee ?? 0) }}</span>
              </div>
              <div class="px-2 pb-1">
                <span class="text-caption text-grey font-italic">Fee = {{ formatCurrency(feeBreakdown.summary.recoveryAmount ?? 0) }} &times; {{ feeBreakdown.pctLabel }}<span v-if="roundToNearestDollar"> - rounded to the nearest dollar</span></span>
              </div>
              <div class="flex justify-between py-[5px] px-2">
                <span class="text-body-2 text-grey-darken-2">Less: {{ expenseLabel }}</span>
                <span class="text-body-2 font-weight-bold tabular-nums">&minus;{{ formatCurrency(feeBreakdown.summary.totalExpenses ?? 0) }}</span>
              </div>
              <div class="flex justify-between py-[5px] px-2 border-t-2 border-t-[#424242] mt-1.5 pt-2 font-bold text-[#24ab10] text-[15px]">
                <span class="text-[#555]">Attorney Fee to Record</span>
                <span class="font-semibold tabular-nums">{{ formatCurrency(feeBreakdown.summary.attorneyFee ?? 0) }}</span>
              </div>
              <div class="flex justify-between py-[5px] px-2" style="opacity: 0.6">
                <span class="text-caption text-grey-darken-1">Client Net from Settlement</span>
                <span class="text-caption font-weight-medium tabular-nums">{{ formatCurrency(feeBreakdown.summary.clientNetRecovery ?? 0) }}</span>
              </div>
            </template>

            <!-- Net -->
            <template v-else-if="feeBreakdown.variant === 'net'">
              <div class="flex justify-between py-[5px] px-2">
                <span class="text-body-2 text-grey-darken-2">Recovery Amount</span>
                <span class="text-body-2 font-weight-bold tabular-nums">{{ formatCurrency(feeBreakdown.summary.recoveryAmount ?? 0) }}</span>
              </div>
              <div class="flex justify-between py-[5px] px-2">
                <span class="text-body-2 text-grey-darken-2">Less: {{ expenseLabel }}</span>
                <span class="text-body-2 font-weight-bold tabular-nums">&minus;{{ formatCurrency(feeBreakdown.summary.totalExpenses ?? 0) }}</span>
              </div>
              <div class="flex justify-between py-[5px] px-2 border-t mt-1 pt-2">
                <span class="text-body-2 font-weight-bold">Net for Fee Calculation</span>
                <span class="text-body-2 font-weight-bold tabular-nums">{{ formatCurrency(feeBreakdown.summary.feeBase ?? 0) }}</span>
              </div>
              <div class="flex justify-between py-[5px] px-2">
                <span class="text-body-2 text-grey-darken-2">Attorney Fee ({{ feeBreakdown.pctLabel }})</span>
                <span class="text-body-2 font-weight-bold tabular-nums">&minus;{{ formatCurrency(feeBreakdown.summary.attorneyFee ?? 0) }}</span>
              </div>
              <div class="px-2 pb-1">
                <span class="text-caption text-grey font-italic">Fee = {{ formatCurrency(feeBreakdown.summary.feeBase ?? 0) }} &times; {{ feeBreakdown.pctLabel }}<span v-if="roundToNearestDollar"> - rounded to the nearest dollar</span></span>
              </div>
              <div class="flex justify-between py-[5px] px-2 border-t-2 border-t-[#424242] mt-1.5 pt-2 font-bold text-[#24ab10] text-[15px]">
                <span class="text-[#555]">Attorney Fee to Record</span>
                <span class="font-semibold tabular-nums">{{ formatCurrency(feeBreakdown.summary.attorneyFee ?? 0) }}</span>
              </div>
              <div class="flex justify-between py-[5px] px-2" style="opacity: 0.6">
                <span class="text-caption text-grey-darken-1">Client Net from Settlement</span>
                <span class="text-caption font-weight-medium tabular-nums">{{ formatCurrency(feeBreakdown.summary.clientNetRecovery ?? 0) }}</span>
              </div>
            </template>

            <!-- Absorbed + Gross -->
            <template v-else-if="feeBreakdown.variant === 'absorbed-gross'">
              <div class="flex justify-between py-[5px] px-2">
                <span class="text-body-2 text-grey-darken-2">Recovery Amount</span>
                <span class="text-body-2 font-weight-bold tabular-nums">{{ formatCurrency(feeBreakdown.summary.recoveryAmount ?? 0) }}</span>
              </div>
              <div class="flex justify-between py-[5px] px-2">
                <span class="text-body-2 text-grey-darken-2">Gross Fee ({{ feeBreakdown.pctLabel }})</span>
                <span class="text-body-2 font-weight-bold tabular-nums">&minus;{{ formatCurrency(feeBreakdown.summary.attorneyFee ?? grossFeeAmount) }}</span>
              </div>
              <div class="px-2 pb-1">
                <span class="text-caption text-grey font-italic">Fee = {{ formatCurrency(feeBreakdown.summary.recoveryAmount ?? 0) }} &times; {{ feeBreakdown.pctLabel }}<span v-if="roundToNearestDollar"> - rounded to the nearest dollar</span></span>
              </div>
              <div class="flex justify-between py-[5px] px-2">
                <span class="text-body-2 text-grey-darken-2">Less: Firm Expenses (absorbed)</span>
                <span class="text-body-2 font-weight-bold tabular-nums">&minus;{{ formatCurrency(feeBreakdown.summary.totalExpenses ?? 0) }}</span>
              </div>
              <div class="flex justify-between py-[5px] px-2 border-t mt-1 pt-2">
                <span class="text-body-2 font-weight-bold">Net Attorney Fee</span>
                <span class="text-body-2 font-weight-bold tabular-nums">{{ netAttorneyFeeDisplay }}</span>
              </div>
              <div class="px-2 pb-1">
                <span class="text-caption text-grey font-italic">Expenses come out of the fee - the client is not charged</span>
              </div>
              <div class="flex justify-between py-[5px] px-2 border-t-2 border-t-[#424242] mt-1.5 pt-2 font-bold text-[#24ab10] text-[15px]">
                <span class="text-[#555]">Attorney Fee to Record</span>
                <span class="font-semibold tabular-nums">{{ formatCurrency(netAttorneyFeeAmount) }}</span>
              </div>
              <div class="flex justify-between py-[5px] px-2" style="opacity: 0.6">
                <span class="text-caption text-grey-darken-1">Client Net from Settlement</span>
                <span class="text-caption font-weight-medium tabular-nums">{{ formatCurrency(feeBreakdown.summary.clientNetRecovery ?? 0) }}</span>
              </div>
            </template>

            <!-- Absorbed + Net -->
            <template v-else-if="feeBreakdown.variant === 'absorbed-net'">
              <div class="flex justify-between py-[5px] px-2">
                <span class="text-body-2 text-grey-darken-2">Recovery Amount</span>
                <span class="text-body-2 font-weight-bold tabular-nums">{{ formatCurrency(feeBreakdown.summary.recoveryAmount ?? 0) }}</span>
              </div>
              <div class="flex justify-between py-[5px] px-2">
                <span class="text-body-2 text-grey-darken-2">Less: Firm Expenses (absorbed)</span>
                <span class="text-body-2 font-weight-bold tabular-nums">&minus;{{ formatCurrency(feeBreakdown.summary.totalExpenses ?? 0) }}</span>
              </div>
              <div class="flex justify-between py-[5px] px-2 border-t mt-1 pt-2">
                <span class="text-body-2 font-weight-bold">Net for Fee Calculation</span>
                <span class="text-body-2 font-weight-bold tabular-nums">{{ formatCurrency(feeBreakdown.summary.feeBase ?? 0) }}</span>
              </div>
              <div class="flex justify-between py-[5px] px-2">
                <span class="text-body-2 text-grey-darken-2">Attorney Fee ({{ feeBreakdown.pctLabel }})</span>
                <span class="text-body-2 font-weight-bold tabular-nums">&minus;{{ formatCurrency(feeBreakdown.summary.attorneyFee ?? 0) }}</span>
              </div>
              <div class="px-2 pb-1">
                <span class="text-caption text-grey font-italic">Fee = {{ formatCurrency(feeBreakdown.summary.feeBase ?? 0) }} &times; {{ feeBreakdown.pctLabel }}<span v-if="roundToNearestDollar"> - rounded to the nearest dollar</span></span>
              </div>
              <div class="flex justify-between py-[5px] px-2">
                <span class="text-body-2 text-grey-darken-2">Less: Total Case Costs (Absorbed)</span>
                <span class="text-body-2 font-weight-bold tabular-nums">&minus;{{ formatCurrency(feeBreakdown.summary.totalExpenses ?? 0) }}</span>
              </div>
              <div class="flex justify-between py-[5px] px-2 border-t mt-1 pt-2">
                <span class="text-body-2 font-weight-bold">Net Attorney Fee</span>
                <span class="text-body-2 font-weight-bold tabular-nums">{{ netAttorneyFeeDisplay }}</span>
              </div>
              <div class="px-2 pb-1">
                <span class="text-caption text-green-darken-2 font-italic">Firm absorbs expenses - costs reduce the fee base and come out of the fee. The client is not charged.</span>
              </div>
              <div class="flex justify-between py-[5px] px-2 border-t-2 border-t-[#424242] mt-1.5 pt-2 font-bold text-[#24ab10] text-[15px]">
                <span class="text-[#555]">Attorney Fee to Record</span>
                <span class="font-semibold tabular-nums">{{ formatCurrency(netAttorneyFeeAmount) }}</span>
              </div>
              <div class="flex justify-between py-[5px] px-2" style="opacity: 0.6">
                <span class="text-caption text-grey-darken-1">Client Net from Settlement</span>
                <span class="text-caption font-weight-medium tabular-nums">{{ formatCurrency(feeBreakdown.summary.clientNetRecovery ?? 0) }}</span>
              </div>
            </template>

          </div>
        </div>

        <!-- Edit fee details expander -->
        <details class="fee-edit-details mb-4" open>
          <summary class="flex items-center gap-[5px] text-[12.5px] font-semibold text-[#555] cursor-pointer select-none list-none py-[5px]">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6" /></svg>
            Edit fee details
          </summary>
          <div class="pt-3 pb-1">
            <div class="d-flex gap-3 flex-wrap align-end">
              <!-- Description -->
              <div class="flex-grow-1" style="min-width: 180px">
                <div class="text-uppercase text-caption font-weight-bold text-grey-darken-1 mb-1">
                  Fee Description
                  <LlTooltip max-width="260" text="Appears on the fixed fee record and the settlement invoice. Max 255 characters.">
                    <template #activator>
                      <v-icon size="14" color="grey" class="cursor-pointer">mdi-information-outline</v-icon>
                    </template>
                  </LlTooltip>
                </div>
                <LlTextField
                  v-model="feeDescription"
                  :maxlength="255"
                  density="compact"
                  hide-details
                />
              </div>
              <!-- Date -->
              <div style="width: 170px; flex-shrink: 0">
                <div class="text-uppercase text-caption font-weight-bold text-grey-darken-1 mb-1">
                  Fee Date
                  <LlTooltip max-width="260" text="The date the fee is recorded. Defaults to today — change to match the settlement or fee agreement date.">
                    <template #activator>
                      <v-icon size="14" color="grey" class="cursor-pointer">mdi-information-outline</v-icon>
                    </template>
                  </LlTooltip>
                </div>
                <LlDatePicker
                  class="fee-date-picker"
                  :model-value="feeDateModel"
                  label=""
                  label-class="d-none"
                  @update:model-value="onFeeDateChange"
                />
              </div>
              <!-- Rounding -->
              <div class="pb-1">
                <LlCheckbox
                  v-model="roundingCheckboxModel"
                  label="Round to whole dollar"
                  hide-details
                  class="ma-0 pa-0"
                />
              </div>
            </div>
          </div>
        </details>

        <!-- Co-Counsel & Referral Fee Recipients (P4.5) — split panel -->
        <details
          v-if="coCounselEnabled && localRecipients.length > 0"
          class="fee-recipients-details mb-4 bg-[#f1faf4] border border-[#bfe0c6] rounded-[10px] px-[18px] py-3"
          open
        >
          <summary class="flex items-center gap-[5px] text-[12.5px] font-semibold text-[#1b5e20] cursor-pointer select-none list-none py-[5px] border-b border-b-[#8ec8a0] pb-[8px]">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6" /></svg>
            Fee Split by Recipient ({{ displayRows.length }} recipients)
          </summary>
          <div class="co-counsel-recipients-section pt-2 text-[13px]">
            <AlertBar
              v-if="percentageSumExceeds100"
              type="error"
              class="mb-2"
            >
              Percentages cannot exceed 100%
            </AlertBar>
            <AlertBar
              v-if="hasZeroOrNegativePercentage"
              type="error"
              class="mb-2"
            >
              Each recipient's percentage must be greater than 0
            </AlertBar>

            <div
              v-for="row in displayRows"
              :key="row.id"
              class="fee-recipient-row flex items-center justify-between gap-3 py-[6px]"
              :class="row.isFirm
                ? 'fee-recipient-row--firm font-weight-bold text-[#1b5e20]'
                : 'border-b border-b-[#d7ebda]'"
            >
              <div>
                <span>{{ row.vendorDisplayName }}</span>
                <span v-if="!row.isFirm" class="text-caption text-grey ml-1">({{ roleLabel(row.role) }})</span>
              </div>
              <div class="flex items-center gap-3">
                <div v-if="!row.isFirm && recipientsEditable" style="width: 130px">
                  <LlTextField
                    class="fee-recipient-percentage-input"
                    :model-value="row.percentage"
                    type="number"
                    density="compact"
                    hide-details
                    suffix="%"
                    @update:model-value="(value) => onPercentageChange(row.id, value)"
                  />
                </div>
                <span v-else>{{ row.percentage.toFixed(4) }}%</span>
                <span class="font-weight-bold" style="min-width: 90px; text-align: right">{{ formatCurrency(row.amount) }}</span>
              </div>
            </div>

            <LlSecondaryButton
              v-if="isRecipientsLocked && !recipientsEditable"
              variant="text"
              color="primary"
              size="small"
              class="edit-recipients-link hover:!text-[rgb(var(--v-theme-primary-darken-1))] hover:underline font-weight-medium d-inline-block mt-2 px-0"
              @click="recipientsEditable = true"
            >
              Edit Recipients
            </LlSecondaryButton>
          </div>
        </details>

      </template>

      <!-- Navigation — old design -->
      <div v-if="!redesignEnabled" class="sett-step-footer">
        <LlSecondaryButton @click="$emit('go-back')">
          &larr; Back
        </LlSecondaryButton>
        <LlSecondaryButton
          color="primary"
          :disabled="!canConfirm"
          :loading="saving"
          @click="$emit('confirm-fee')"
        >
          Confirm Attorney Fee ({{ formatCurrency(calculatedFee ?? 0) }}) &rarr;
        </LlSecondaryButton>
      </div>

      <!-- Navigation — redesign -->
      <div v-else class="sett-step-footer">
        <LlSecondaryButton @click="$emit('go-back')">
          &larr; Back
        </LlSecondaryButton>
        <LlSecondaryButton
          color="primary"
          :disabled="hasNegativeFeeBase || loading || calculatedFee == null || percentageSumExceeds100"
          :loading="recording || saving"
          @click="onAutoConfirm"
        >
          Confirm Attorney Fee &rarr;
        </LlSecondaryButton>
      </div>

    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { format, isValid, parseISO } from 'date-fns';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlCheckbox from '@/components/LlCheckbox.vue';
import LlTextField from '@/components/LlTextField.vue';
import LlDatePicker from '@/components/LlDatePicker.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import LlIcon from '@/components/LlIcon.vue';
import AlertBar from '@/components/AlertBar.vue';
import { formatCurrency } from '@/services/utils';
import type { FeeRecipientRole, FeeRecipient } from '@/models/settlementTypes';
import { roleLabel } from '@/utils/roleLabels';

interface FeeBreakdownSummary {
  recoveryAmount?: number | null;
  attorneyFee?: number | null;
  unreimbursedExpenses?: number | null;
  totalExpenses?: number | null;
  alreadyPaidByClient?: number | null;
  feeBase?: number | null;
  netAttorneyFee?: number | null;
  clientNetRecovery?: number | null;
  generatingFirmRetainedAmount?: number | null;
  generatingFirmRetainedPercentage?: number | null;
}

interface FeeBreakdown {
  variant: string;
  pctLabel: string;
  summary: FeeBreakdownSummary | null;
}

interface FeeEntry {
  chargeId: string;
  description: string;
  amount: number;
  status: string;
  isSettlementFee: boolean;
  feeDate?: string | null;
}

function todayIso(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

function toIsoDate(value: string | null | undefined): string {
  if (!value) return todayIso();
  return value.slice(0, 10);
}

function formatFeeDate(value: string | null | undefined): string {
  if (!value) return '';
  const parsed = parseISO(value);
  return isValid(parsed) ? format(parsed, 'MM/dd/yyyy') : value.slice(0, 10);
}

const props = defineProps({
  redesignEnabled: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  recording: { type: Boolean, default: false },
  calculatedFee: { type: Number, default: null },
  existingFeeAmount: { type: Number, default: 0 },
  existingFees: { type: Array as () => FeeEntry[], default: (): FeeEntry[] => [] },
  feeMatchStatus: { type: String as () => 'match' | 'mismatch' | 'none', default: 'none' },
  canConfirm: { type: Boolean, default: false },
  hasNegativeFeeBase: { type: Boolean, default: false },
  feeBreakdown: { type: Object as () => FeeBreakdown, required: true },
  expenseLabel: { type: String, default: 'Expenses' },
  expenseArrangement: { type: String, default: 'Firm' },
  feePercentage: { type: Number, default: 0 },
  roundToNearestDollar: { type: Boolean, default: false },
  selectedChargeId: { type: String, default: null },
  coCounselEnabled: { type: Boolean, default: false },
  feeRecipients: { type: Array as () => FeeRecipient[], default: (): FeeRecipient[] => [] },
  isRecipientsConfirmed: { type: Boolean, default: false },
  isRecipientsLocked: { type: Boolean, default: false },
  noMatterRecipientsConfigured: { type: Boolean, default: false },
  firmName: { type: String, default: 'Your Firm' },
});

const emit = defineEmits([
  'confirm-fee',
  'go-back',
  'record-fee',
  'rounding-change',
  'manage-fixed-fees',
  'select-fee',
  'load-fee-recipients',
  'update:feeRecipients',
  'confirm-fee-recipients',
  'auto-confirm-fee',
]);

// ── Redesign state ────────────────────────────────────────
/** Controls visibility of the collapsible calculation breakdown */
const showCalc = ref(false);

/** A 0% contingency fee — not necessarily a formal pro bono designation, just what the notice below describes. */
const isZeroFee = computed(() => (props.feePercentage ?? 0) === 0);

// ── Shared form state ─────────────────────────────────────
const roundingCheckboxModel = computed({
  get: () => props.roundToNearestDollar,
  set: (value: boolean) => emit('rounding-change', value),
});

/**
 * Pre-fill description/date from the existing settlement fee when revisiting
 * the wizard so prior customizations aren't lost. Falls back to defaults.
 */
const existingSettlementFee = computed(() =>
  props.existingFees.find((f) => f.isSettlementFee) ?? null,
);

const feeDescription = ref<string>(
  existingSettlementFee.value?.description || 'Attorney Fee',
);
const feeDate = ref<string>(toIsoDate(existingSettlementFee.value?.feeDate));

// Show the description/date/create row only until a settlement fee exists (old design only).
const showCreateFeeForm = computed(() => !existingSettlementFee.value);

watch(existingSettlementFee, (fee) => {
  if (fee) {
    feeDescription.value = fee.description || 'Attorney Fee';
    feeDate.value = toIsoDate(fee.feeDate);
  }
});

/** Old design: create fee separately then confirm */
function onCreateFee() {
  emit('record-fee', {
    description: feeDescription.value.trim() || 'Attorney Fee',
    feeDate: feeDate.value || todayIso(),
  });
}

/** Redesign: auto-create and confirm in a single action. confirm-fee-recipients
 *  fires first so a parent that syncs it into store state (as ours does) sees
 *  the final recipient set before building the auto-confirm-fee API payload. */
function onAutoConfirm() {
  if (localRecipients.value.length > 0) {
    emit('confirm-fee-recipients', localRecipients.value);
  }
  emit('auto-confirm-fee', {
    description: feeDescription.value.trim() || 'Attorney Fee',
    feeDate: feeDate.value || todayIso(),
  });
}

/** LlDatePicker works in Date objects; we persist as "YYYY-MM-DD" strings. */
const feeDateModel = computed<Date | null>(() => {
  if (!feeDate.value) return null;
  const parsed = parseISO(feeDate.value);
  return isValid(parsed) ? parsed : null;
});

function onFeeDateChange(value: Date | null) {
  feeDate.value = value ? format(value, 'yyyy-MM-dd') : todayIso();
}

/** Amount of the currently selected fee (for mismatch banner in old design). */
const selectedFeeAmount = computed(() => {
  if (!props.selectedChargeId) return 0;
  const fee = props.existingFees.find((f) => f.chargeId === props.selectedChargeId);
  return fee?.amount ?? 0;
});

const grossFeeAmount = computed(() =>
  (props.feeBreakdown.summary?.recoveryAmount ?? 0) * (props.feePercentage ?? 0) / 100,
);

/** Net Attorney Fee (fee minus absorbed expenses) — can go negative when expenses exceed the fee. */
const netAttorneyFeeAmount = computed(
  () => props.feeBreakdown.summary.netAttorneyFee ?? props.feeBreakdown.summary.attorneyFee ?? 0,
);
/** formatCurrency already parenthesizes negatives, so only prepend the minus sign when positive —
 *  otherwise an underwater fee renders as the double-negative "-($X.XX)". */
const netAttorneyFeeDisplay = computed(() =>
  netAttorneyFeeAmount.value < 0
    ? formatCurrency(netAttorneyFeeAmount.value)
    : `−${formatCurrency(netAttorneyFeeAmount.value)}`,
);

// ── Co-Counsel & Referral Fee Recipients (P4.5) ────────────
/** Local editable copy — kept in sync with the prop, but edited independently
 *  so percentage input reactivity doesn't require round-tripping through the
 *  parent on every keystroke. */
const localRecipients = ref<FeeRecipient[]>(props.feeRecipients.map((r) => ({ ...r })));

watch(
  () => props.feeRecipients,
  (recipients) => {
    localRecipients.value = recipients.map((r) => ({ ...r }));
  },
);

/** Percentage inputs stay editable pre-confirm; once locked, "Edit Recipients"
 *  re-enables them (VR-015) without touching isRecipientsLocked itself. */
const recipientsEditable = ref(!props.isRecipientsLocked);
watch(
  () => props.isRecipientsLocked,
  (locked) => {
    recipientsEditable.value = !locked;
  },
);

function onPercentageChange(id: string, value: string | number) {
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  const idx = localRecipients.value.findIndex((r) => r.id === id);
  if (idx === -1) return;
  localRecipients.value[idx] = {
    ...localRecipients.value[idx],
    percentage: Number.isFinite(parsed) ? parsed : 0,
    // The old confirmed dollar amount is stale the instant this row's own percentage
    // is retyped — null it out so recipientShareAmount recomputes live from the new
    // percentage instead of showing a frozen pre-edit amount next to it.
    calculatedShareAmount: null,
  };
  emit('update:feeRecipients', localRecipients.value);
}

const attorneyFeePool = computed(() => props.calculatedFee ?? 0);

/** Each recipient's actual dollar share — the backend's confirmed, FLOOR-rounded
 *  calculatedShareAmount once Step 3 has been confirmed, or a pool×percentage
 *  preview before that (mirrors the per-row amount shown in displayRows).
 *  onPercentageChange nulls out calculatedShareAmount for a row the instant its
 *  own percentage is retyped, so an edited row falls through to the live preview
 *  here while every other still-confirmed row keeps showing its authoritative
 *  backend amount. */
function recipientShareAmount(r: FeeRecipient): number {
  return r.calculatedShareAmount ?? (attorneyFeePool.value * (r.percentage || 0)) / 100;
}

/** Locally-derived preview: BR-010 says the firm retains whatever the FLOOR-rounded
 *  splits leave over, not a clean "100% − sum(percentages)" fraction of the pool —
 *  deriving it from actual per-recipient share amounts (rather than raw percentages)
 *  surfaces that penny-level residual correctly once shares are backend-confirmed.
 *  Math.max guards against a negative preview if percentages are (transiently,
 *  pre-validation) over 100. Used pre-confirm, and as a fallback if the backend
 *  summary hasn't loaded a value yet. */
const derivedRetainedAmount = computed(() =>
  Math.max(
    0,
    attorneyFeePool.value -
      localRecipients.value.reduce((sum, r) => sum + recipientShareAmount(r), 0),
  ),
);
const derivedRetainedPercentage = computed(() =>
  attorneyFeePool.value > 0 ? (derivedRetainedAmount.value / attorneyFeePool.value) * 100 : 0,
);

/** Once recipients are locked (confirmed) AND not currently being re-edited,
 *  summary.generatingFirmRetainedAmount is the backend's authoritative value —
 *  read it directly instead of re-deriving. Otherwise (pre-confirm, or the user
 *  has clicked "Edit Recipients" and is actively typing new percentages) that
 *  same backend field reflects only the matter's last-SAVED percentages, not
 *  whatever the user is currently typing, so the local derivation is used
 *  instead — this is what keeps the firm's row live while editing. */
const retainedAmount = computed(() => {
  const backendValue = props.feeBreakdown.summary?.generatingFirmRetainedAmount;
  return props.isRecipientsLocked && !recipientsEditable.value && backendValue != null
    ? backendValue
    : derivedRetainedAmount.value;
});
const retainedPercentage = computed(() => {
  const backendValue = props.feeBreakdown.summary?.generatingFirmRetainedPercentage;
  return props.isRecipientsLocked && !recipientsEditable.value && backendValue != null
    ? backendValue
    : derivedRetainedPercentage.value;
});

// Defensive coCounselEnabled gate, independent of whether feeRecipients happens to be
// populated — matches this feature's flag-check-everywhere convention (backend, PDF
// renderer, etc.). Both feed the Confirm button's disabled state, so a stale/leftover
// feeRecipients array must never block confirming for a company with the flag off.
/** True when the fee is being split with at least one outside co-counsel/referring
 *  recipient — the "Existing Fee Will Be Updated" mismatch banner compares against
 *  the old single-recipient fee amount, which isn't a meaningful comparison once
 *  the fee is split (BR-mismatch-vs-split). */
const isFeeSplit = computed(
  () => props.coCounselEnabled && localRecipients.value.length > 0,
);

const percentageSumExceeds100 = computed(
  () =>
    props.coCounselEnabled &&
    localRecipients.value.reduce((sum, r) => sum + (r.percentage || 0), 0) > 100,
);
const hasZeroOrNegativePercentage = computed(
  () => props.coCounselEnabled && localRecipients.value.some((r) => (r.percentage ?? 0) <= 0),
);

interface FeeSplitRow {
  id: string;
  vendorDisplayName: string;
  role: FeeRecipientRole | null;
  percentage: number;
  amount: number;
  isFirm: boolean;
}

/** Outside recipients in the order given (no re-sort by share — that would
 *  reorder a row out from under the user's cursor mid-edit), with the generating
 *  firm's own retained share appended as the final row (BR-010/BR-017) — one
 *  unified list, matching the "Fee Split by Recipient" panel design. */
const displayRows = computed<FeeSplitRow[]>(() => {
  const outside = localRecipients.value
    .map((r) => ({
      id: r.id,
      vendorDisplayName: r.vendorDisplayName,
      role: r.role,
      percentage: r.percentage,
      // Prefer the backend's confirmed, FLOOR-rounded share once Step 3 has
      // actually been confirmed — a client-side pool×percentage recalculation
      // is only a preview and can diverge by a cent from the real value.
      amount: recipientShareAmount(r),
      isFirm: false,
    }));
  const firmRow: FeeSplitRow = {
    id: '__firm__',
    vendorDisplayName: props.firmName,
    role: null,
    percentage: retainedPercentage.value,
    amount: retainedAmount.value,
    isFirm: true,
  };
  return [...outside, firmRow];
});

/** Deliberately does NOT gate on noMatterRecipientsConfigured — that flag can be
 *  stale-true from an earlier empty fetch (e.g. a recipient was since added via
 *  the Fee Recipients tab), and a stale "already checked, found nothing" shouldn't
 *  permanently block re-fetching. Reactive (not onMounted-only) so a later prop
 *  change that makes this true again — e.g. feeRecipients going back to empty —
 *  triggers another attempt, not just the first render. */
const shouldLoadFeeRecipients = computed(
  () => props.coCounselEnabled && props.feeRecipients.length === 0 && !props.isRecipientsConfirmed,
);
watch(
  shouldLoadFeeRecipients,
  (should) => {
    if (should) emit('load-fee-recipients');
  },
  { immediate: true },
);
</script>

<style scoped>
/* ── Shared ─────────────────────────────────────────────── */
.fee-date-picker :deep(.v-input__details) {
  display: none;
}

/* Tailwind's `list-none` only clears the standard ::marker triangle;
   Safari/WebKit renders <summary> markers via this vendor pseudo-element
   instead, which Tailwind has no utility for. */
.fee-edit-details summary::-webkit-details-marker,
.fee-recipients-details summary::-webkit-details-marker {
  display: none;
}

/* The chevron svg is a static "pointing down" path — rotate it to point right
   when collapsed so it actually reflects open/closed state, not just a static icon. */
.fee-edit-details summary svg,
.fee-recipients-details summary svg {
  transition: transform 0.15s ease;
  flex-shrink: 0;
}
.fee-edit-details:not([open]) summary svg,
.fee-recipients-details:not([open]) summary svg {
  transform: rotate(-90deg);
}
</style>
